/**
 * The title field, and the engine that fills in everything derived from it.
 *
 * The engine lives here for one reason: the title is the only field guaranteed
 * to be on screen. React effects only run for mounted components, and the
 * fields most worth deriving are the ones sitting in a panel an editor never
 * opens, so hanging the cascade off any of them would mean it ran exactly when
 * it was least needed.
 *
 * The summary underneath is the other half of the deal. The CMS is writing
 * nine fields on the editor's behalf, and the editor still signs off the page,
 * so what was written is stated in one place they cannot miss rather than left
 * for them to discover inside a collapsed section.
 */

import React from "react";
import { FieldHelperText, FieldProps } from "@firecms/core";
import { Chip, DebouncedTextField, Typography } from "@firecms/ui";

import {
    DERIVED_FIELDS,
    DerivableKey,
    deriveValues,
    GuideValues,
    isEmptyValue
} from "../derive/autofill";
import { formKeyOf, SITE_ORIGIN, useAutofill, useTracking } from "./useDerivation";

const display = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "—";
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
};

/** Reading time is a number of minutes; everything else reads as it is stored. */
const displayFor = (key: DerivableKey, value: unknown): string =>
    key === "readingTime" && typeof value === "number" ? `${value} min` : display(value);

function AutofillSummary({ values, formKey }: { values: GuideValues; formKey: string }) {
    const following = useTracking(formKey);
    const { resolved } = deriveValues(values, new Set(following), SITE_ORIGIN);

    const rows = DERIVED_FIELDS.map((field) => {
        const current = values[field.key];
        return {
            key: field.key,
            label: field.label,
            shown: displayFor(field.key, isEmptyValue(current) ? resolved[field.key] : current),
            auto: following.has(field.key)
        };
    });

    return (
        <div className={"flex flex-col gap-2 rounded-md border border-surface-accent-200 dark:border-surface-accent-700 p-3"}>
            <Typography variant={"label"}>Filled in from what you have typed</Typography>
            <div className={"flex flex-col gap-1"}>
                {rows.map((row) => (
                    <div key={row.key} className={"flex flex-row gap-2 items-baseline"}>
                        <Typography variant={"caption"} className={"opacity-60 w-40 shrink-0"}>
                            {row.label}
                        </Typography>
                        <Typography variant={"caption"} className={"font-mono break-all"}>
                            {row.shown}
                        </Typography>
                        {!row.auto && (
                            <Chip size={"small"}>yours</Chip>
                        )}
                    </div>
                ))}
            </div>
            <Typography variant={"caption"} className={"opacity-70"}>
                Each of these is an editable field below. Change one and it stops following.
                The keywords are counted from your own wording, not from search data, so read
                them before publishing.
            </Typography>
        </div>
    );
}

export function GuideTitleField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    includeDescription,
    autoFocus,
    context
}: FieldProps<string>) {

    useAutofill(context);

    return (
        <div className={"flex flex-col gap-2"}>
            <DebouncedTextField
                value={value ?? ""}
                label={property.name}
                autoFocus={autoFocus ?? false}
                disabled={disabled ?? false}
                error={Boolean(showError && error)}
                onChange={(event) => setValue(event.target.value)}/>
            <FieldHelperText error={error}
                             showError={showError ?? false}
                             includeDescription={includeDescription ?? true}
                             property={property}/>
            <AutofillSummary values={context.values as GuideValues} formKey={formKeyOf(context)}/>
        </div>
    );
}
